import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import Navigation from "@/components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShoppingCart, BookOpen, Package, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { PrintifyStore } from "@/components/PrintifyStore";
import type { MarketplaceProduct } from "@shared/schema";

export default function Marketplace() {
  const { user, hasActiveSubscription } = useAuth();
  const { toast } = useToast();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [processingProductId, setProcessingProductId] = useState<number | null>(null);
  const lastClickTimeRef = useRef<number>(0);

  const { data: products, isLoading } = useQuery<MarketplaceProduct[]>({
    queryKey: ['/api/marketplace/products'],
  });

  const createCheckoutMutation = useMutation({
    mutationFn: async (items: { productId: number; quantity: number }[]) => {
      const response = await apiRequest("POST", "/api/marketplace/checkout", { items });
      return await response.json();
    },
    onSuccess: async (data: any) => {
      console.log('Checkout API response:', data);
      setProcessingProductId(null);
      if (data.checkoutUrl) {
        console.log('Redirecting to checkout URL:', data.checkoutUrl);
        window.location.href = data.checkoutUrl;
      } else {
        console.error('No checkoutUrl in response:', data);
        toast({
          title: "Checkout Error",
          description: "No checkout URL received from server",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      setProcessingProductId(null);
      toast({
        title: "Checkout Failed",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const handlePurchase = (product: MarketplaceProduct, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      // Note: stopImmediatePropagation is not available on React synthetic events
    }

    // Debounce clicks - prevent rapid successive clicks
    const now = Date.now();
    if (now - lastClickTimeRef.current < 1000) {
      console.log('Debouncing click for product:', product.id);
      return;
    }
    lastClickTimeRef.current = now;

    // Prevent multiple calls - check both product-specific and global state
    if (processingProductId !== null || createCheckoutMutation.isPending) {
      console.log('Purchase already in progress, blocking request for:', product.id);
      return;
    }

    console.log('Processing purchase for product:', product.id, product.name);
    setProcessingProductId(product.id);
    createCheckoutMutation.mutate([{ productId: product.id, quantity: 1 }]);
  };

  if (isLoading) {
    return (
      <>
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i}>
                <Skeleton className="h-48 w-full" />
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full mt-2" />
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </>
    );
  }

  const filteredProducts = products?.filter(p => 
    selectedCategory === "all" || p.category === selectedCategory
  ) || [];

  const categories = ["all", "swag", "handbook", "physical_book"];

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-teal-50">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 sm:mb-12"
          >
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent mb-3 sm:mb-4">
              Marketplace
            </h1>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Bring the magic of storytelling into your everyday life with our exclusive collection
            </p>
          </motion.div>

          {/* Category Tabs */}
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-8">
            <TabsList className="grid w-full max-w-5xl mx-auto grid-cols-2 sm:grid-cols-5 gap-1 h-auto p-1">
              <TabsTrigger value="all" className="text-xs sm:text-sm px-2 py-2 h-auto whitespace-nowrap">
                All Products
              </TabsTrigger>
              <TabsTrigger value="merchandise" className="text-xs sm:text-sm px-2 py-2 h-auto whitespace-nowrap">
                Merchandise
              </TabsTrigger>
              <TabsTrigger value="swag" className="text-xs sm:text-sm px-2 py-2 h-auto whitespace-nowrap">
                Story Swag
              </TabsTrigger>
              <TabsTrigger value="handbook" className="text-xs sm:text-sm px-2 py-2 h-auto whitespace-nowrap">
                Handbooks
              </TabsTrigger>
              <TabsTrigger value="physical_book" className="text-xs sm:text-sm px-2 py-2 h-auto whitespace-nowrap">
                Story Books
              </TabsTrigger>
            </TabsList>

            {/* Merchandise Tab Content */}
            {selectedCategory === "merchandise" ? (
              <div className="mt-6">
                <PrintifyStore />
              </div>
            ) : (
              <div className="mt-6">
                {/* Products Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProducts.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card className="h-full hover:shadow-xl transition-shadow duration-300">
                        {product.imageUrl && (
                          <div className="relative h-48 overflow-hidden rounded-t-lg">
                            <img
                              src={product.imageUrl}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                            {product.category === "swag" && (
                              <Badge className="absolute top-2 right-2 bg-purple-600">
                                <img src="/logo.png" alt="SnuggleCrafters Logo" className="w-3 h-3 mr-1 object-contain" />
                                Exclusive
                              </Badge>
                            )}
                          </div>
                        )}
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            {product.category === "handbook" && <BookOpen className="w-5 h-5 text-teal-600" />}
                            {product.category === "swag" && <Package className="w-5 h-5 text-purple-600" />}
                            {product.category === "physical_book" && <img src="/logo.png" alt="SnuggleCrafters Logo" className="w-5 h-5 object-contain" />}
                            {product.name}
                          </CardTitle>
                          <CardDescription>{product.description}</CardDescription>
                        </CardHeader>
                        <CardContent className="flex flex-col gap-4">
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-bold text-purple-600">
                              ${product.price}
                            </span>
                            {product.stockQuantity && product.stockQuantity < 10 && (
                              <Badge variant="secondary">
                                Only {product.stockQuantity} left!
                              </Badge>
                            )}
                          </div>
                          <Button
                            onClick={(e) => handlePurchase(product, e)}
                            disabled={processingProductId === product.id}
                            className="w-full bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700"
                          >
                            <ShoppingCart className="mr-2 h-4 w-4" />
                            {processingProductId === product.id ? "Processing..." : "Buy Now"}
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>

                {filteredProducts.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-500 text-lg">No products available in this category yet.</p>
                    <p className="text-gray-400 mt-2">Check back soon for new additions!</p>
                  </div>
                )}
              </div>
            )}
          </Tabs>

          {/* Special Offer Banner */}
          {hasActiveSubscription && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-12 bg-gradient-to-r from-purple-100 to-teal-100 rounded-2xl p-8 text-center"
            >
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Premium Member Exclusive! 🌟
              </h3>
              <p className="text-gray-600">
                As a premium subscriber, enjoy 20% off all marketplace purchases
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
}